import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';

import { DividendsAndInterestDocument } from './graphql/dividendsAndInterest.generated';

import { DateRangeOption } from '../../../../graphql/schema';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { dateRangeOptions } from '../../../shared/date-range-filter/DateRangeFilter';

interface DividendsAndInterestCardViewProps {
  dateRangeOption?: DateRangeOption;
}

const DividendsAndInterestCardView: React.FC<DividendsAndInterestCardViewProps> = ({
  dateRangeOption = DateRangeOption.AllTime,
}) => {
  const { data } = useGraphQLQuery(DividendsAndInterestDocument, {
    variables: { dateRangeOption },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { investing, interest } = data.performance.totals;

  if (!investing?.dividends && !interest)
    return (
      <Card>
        <Card.Header css={{ padding: 'space.300' }}>
          <H2 variant="title200">Dividend and interest income</H2>
        </Card.Header>
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <Card.Content css={{ padding: 'space.300' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
            <Text>You don't have any accounts earning dividends or interest.</Text>
            <SambaAutoLink to={urlBuilder.addGoal.absolute()} variant="textPrimary">
              Open an account
            </SambaAutoLink>
          </Box>
        </Card.Content>
      </Card>
    );

  const cashInterestPaid = interest ? interest.paid : Money(0);
  const dividendsEarned = investing?.dividends ? investing.dividends.total : Money(0);
  const totalIncome = Money(dividendsEarned.cents + cashInterestPaid.cents);
  const dateRangeLabel = dateRangeOptions.find(option => option.value === dateRangeOption)?.longLabel || '';

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">Dividend and interest income</H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: 'space.50' }}>
          <Text>Income {dateRangeLabel}</Text>
          <Text variant="text300" css={{ color: totalIncome.cents > 0 ? 'text.success' : 'text.default' }}>
            {formatMoney(totalIncome)}
          </Text>
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Cash interest paid</Text>
          <Text>{formatMoney(cashInterestPaid)}</Text>
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Dividends earned</Text>
          <Text>{formatMoney(dividendsEarned)}</Text>
        </Box>
      </Card.Content>
    </Card>
  );
};

export const DividendsAndInterestCard: React.FC<DividendsAndInterestCardViewProps> = props => (
  <ErrorBoundary fallbackType="none">
    <DividendsAndInterestCardView {...props} />
  </ErrorBoundary>
);
